/**
 * Common Concerns Content - Uro2Go
 * 
 * Patient education articles for common urological concerns.
 * All content at 8th-grade reading level.
 * Both English and Spanish versions provided.
 */

export const commonConcernsArticles = [
  {
    id: 'blood-in-semen',
    title: {
      en: 'Blood in Semen – What It Means',
      es: 'Sangre en el Semen – Qué Significa'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/9/97/Gray1160.png',
      alt: {
        en: 'Anatomical diagram of the prostate and seminal vesicles',
        es: 'Diagrama anatómico de la próstata y vesículas seminales'
      },
      source: 'Gray\'s Anatomy / Wikimedia Commons (Public Domain)',
      caption: {
        en: 'The prostate and seminal vesicles, where blood in semen often originates',
        es: 'La próstata y las vesículas seminales, donde a menudo se origina la sangre en el semen'
      }
    },
    tags: ['blood in semen', 'hemospermia', 'semen blood', 'prostate', 'sexual health'],
    relatedTopics: ['prostate-biopsy', 'prostatitis', 'prostate'],
    content: {
      en: `Seeing blood in semen, also called hemospermia, can be alarming. Most men immediately worry that something serious is wrong, but the good news is that blood in semen is usually harmless. In many cases, it happens because of irritation or inflammation somewhere along the reproductive tract, such as the prostate, seminal vesicles, or urethra. Even minor trauma, recent sexual activity, or a medical procedure like a prostate biopsy can cause temporary blood in the semen.

In younger men, especially those under 40, blood in semen is rarely linked to serious disease. Infections of the prostate or urethra are common causes. In older men, particularly those over 50, doctors may look more closely to rule out prostate problems, including prostate cancer, although this is still an uncommon cause.

Most episodes of blood in semen go away on their own within a few weeks. If it happens just once or twice and you feel fine otherwise, it usually does not require treatment. However, you should seek medical care if the problem keeps happening, if you also have pain, fever, urinary symptoms, or blood in the urine. A simple evaluation by a urologist can usually determine the cause and provide reassurance.`,
      es: `Ver sangre en el semen, también llamado hemospermia, puede ser alarmante. La mayoría de los hombres se preocupan de inmediato pensando que algo grave está mal, pero la buena noticia es que la sangre en el semen generalmente es inofensiva. En muchos casos, ocurre debido a irritación o inflamación en alguna parte del tracto reproductivo, como la próstata, las vesículas seminales o la uretra. Incluso un trauma menor, actividad sexual reciente o un procedimiento médico como una biopsia de próstata pueden causar sangre temporal en el semen.

En hombres más jóvenes, especialmente los menores de 40 años, la sangre en el semen rara vez está relacionada con enfermedades graves. Las infecciones de la próstata o la uretra son causas comunes. En hombres mayores, particularmente los mayores de 50 años, los médicos pueden examinar más de cerca para descartar problemas de próstata, incluyendo cáncer de próstata, aunque esta sigue siendo una causa poco común.

La mayoría de los episodios de sangre en el semen desaparecen por sí solos en unas pocas semanas. Si ocurre solo una o dos veces y te sientes bien por lo demás, generalmente no requiere tratamiento. Sin embargo, debes buscar atención médica si el problema sigue ocurriendo, si también tienes dolor, fiebre, síntomas urinarios o sangre en la orina. Una evaluación simple por un urólogo generalmente puede determinar la causa y proporcionar tranquilidad.`
    },
    whenToCall: {
      en: [
        'Blood in semen that keeps happening (more than a few weeks)',
        'Pain during ejaculation or urination',
        'Fever or chills',
        'Blood in urine',
        'Difficulty urinating'
      ],
      es: [
        'Sangre en el semen que sigue ocurriendo (más de unas pocas semanas)',
        'Dolor durante la eyaculación o al orinar',
        'Fiebre o escalofríos',
        'Sangre en la orina',
        'Dificultad para orinar'
      ]
    }
  },
  {
    id: 'cloudy-urine',
    title: {
      en: 'Cloudy Urine – When to Worry',
      es: 'Orina Turbia – Cuándo Preocuparse'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6d/Urinary_System_%28Male%29.png/500px-Urinary_System_%28Male%29.png',
      alt: {
        en: 'Diagram of the male urinary system',
        es: 'Diagrama del sistema urinario masculino'
      },
      source: 'Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'The urinary system produces and excretes urine from the body',
        es: 'El sistema urinario produce y excreta la orina del cuerpo'
      }
    },
    tags: ['cloudy urine', 'urine appearance', 'urinary infection', 'dehydration', 'uti'],
    relatedTopics: ['uti', 'recurrent-uti', 'bladder'],
    content: {
      en: `Many people notice cloudy urine from time to time and assume it means they have an infection. While cloudy urine can be a sign of a urinary tract infection, it is often caused by much simpler and less concerning things. Dehydration is one of the most common reasons urine looks cloudy. When you are not drinking enough water, urine becomes more concentrated and can appear darker or hazy.

Diet can also affect the appearance of urine. Certain foods, vitamins, or supplements may change urine color or clarity. Vaginal discharge in women or normal mucus in the urinary tract can mix with urine and create a cloudy look. Even harmless crystals that form naturally in urine can make it appear less clear.

Cloudy urine becomes more concerning if it occurs along with symptoms such as burning with urination, frequent urination, strong odor, pelvic pain, fever, or blood in the urine. These signs may indicate a urinary tract infection, kidney stones, or another medical issue that needs treatment.

If cloudy urine happens occasionally and you feel fine, increasing water intake is often all that is needed. If it persists or is accompanied by other symptoms, a simple urine test can help determine whether there is an infection or another problem that requires medical attention.`,
      es: `Muchas personas notan orina turbia de vez en cuando y asumen que significa que tienen una infección. Aunque la orina turbia puede ser un signo de infección del tracto urinario, a menudo es causada por cosas mucho más simples y menos preocupantes. La deshidratación es una de las razones más comunes por las que la orina se ve turbia. Cuando no estás bebiendo suficiente agua, la orina se vuelve más concentrada y puede aparecer más oscura o nebulosa.

La dieta también puede afectar la apariencia de la orina. Ciertos alimentos, vitaminas o suplementos pueden cambiar el color o la claridad de la orina. El flujo vaginal en las mujeres o el moco normal en el tracto urinario pueden mezclarse con la orina y crear una apariencia turbia. Incluso los cristales inofensivos que se forman naturalmente en la orina pueden hacer que parezca menos clara.

La orina turbia se vuelve más preocupante si ocurre junto con síntomas como ardor al orinar, micción frecuente, olor fuerte, dolor pélvico, fiebre o sangre en la orina. Estos signos pueden indicar una infección del tracto urinario, cálculos renales u otro problema médico que necesita tratamiento.

Si la orina turbia ocurre ocasionalmente y te sientes bien, aumentar la ingesta de agua es a menudo todo lo que se necesita. Si persiste o está acompañada de otros síntomas, una simple prueba de orina puede ayudar a determinar si hay una infección u otro problema que requiera atención médica.`
    },
    whenToCall: {
      en: [
        'Burning or pain when urinating',
        'Frequent urination or urgency',
        'Fever or chills',
        'Blood in urine',
        'Lower back or pelvic pain',
        'Cloudy urine that does not improve with more water intake'
      ],
      es: [
        'Ardor o dolor al orinar',
        'Micción frecuente o urgencia',
        'Fiebre o escalofríos',
        'Sangre en la orina',
        'Dolor en la parte baja de la espalda o pélvico',
        'Orina turbia que no mejora con mayor ingesta de agua'
      ]
    }
  },
  {
    id: 'scrotal-cysts',
    title: {
      en: 'Scrotal Cysts Explained',
      es: 'Quistes Escrotales Explicados'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/e/ef/Hydrocele.png',
      alt: {
        en: 'Diagram showing a hydrocele around the testicle',
        es: 'Diagrama que muestra un hidrocele alrededor del testículo'
      },
      source: 'Wikimedia Commons (CC BY-SA 3.0)',
      caption: {
        en: 'A hydrocele is fluid collection around the testicle, one type of benign scrotal swelling',
        es: 'Un hidrocele es una acumulación de líquido alrededor del testículo'
      }
    },
    tags: ['scrotal cyst', 'epididymal cyst', 'spermatocele', 'hydrocele', 'testicular lump', 'scrotum'],
    relatedTopics: ['testicle', 'hydrocele-spermatocele', 'testicular-cancer'],
    content: {
      en: `Finding a lump in the scrotum can be frightening, but many scrotal lumps are actually harmless cysts. The most common types are epididymal cysts and spermatoceles. These are small fluid-filled sacs that develop in the epididymis, the structure that sits behind the testicle and stores sperm. They are not cancerous and usually do not affect fertility or sexual function.

Scrotal cysts often feel like smooth, round, soft lumps separate from the testicle itself. They may be discovered accidentally during a self-exam or while showering. Most cysts do not cause pain and do not require any treatment.

Hydroceles are another common scrotal condition. A hydrocele is a collection of fluid around the testicle that causes swelling of the scrotum. Like cysts, hydroceles are usually benign and painless.

The most important step when noticing any new scrotal lump is to have it evaluated by a medical professional. A simple physical exam and scrotal ultrasound can easily tell the difference between a harmless cyst and something more serious like a testicular tumor. Treatment is only needed if a cyst becomes large, painful, or bothersome.`,
      es: `Encontrar un bulto en el escroto puede ser aterrador, pero muchos bultos escrotales son en realidad quistes inofensivos. Los tipos más comunes son los quistes epididimarios y los espermatoceles. Estos son pequeños sacos llenos de líquido que se desarrollan en el epidídimo, la estructura que se encuentra detrás del testículo y almacena esperma. No son cancerosos y generalmente no afectan la fertilidad ni la función sexual.

Los quistes escrotales a menudo se sienten como bultos suaves, redondos y blandos separados del testículo en sí. Pueden descubrirse accidentalmente durante un autoexamen o mientras te duchas. La mayoría de los quistes no causan dolor y no requieren ningún tratamiento.

Los hidroceles son otra condición escrotal común. Un hidrocele es una acumulación de líquido alrededor del testículo que causa hinchazón del escroto. Como los quistes, los hidroceles generalmente son benignos e indoloros.

El paso más importante al notar cualquier bulto escrotal nuevo es que lo evalúe un profesional médico. Un simple examen físico y una ecografía escrotal pueden distinguir fácilmente entre un quiste inofensivo y algo más serio como un tumor testicular. El tratamiento solo es necesario si un quiste se vuelve grande, doloroso o molesto.`
    },
    whenToCall: {
      en: [
        'Any new lump in the scrotum (even if painless)',
        'Rapid growth of a scrotal lump',
        'Pain or tenderness in the testicle',
        'Heaviness or aching in the scrotum',
        'Swelling that does not go away'
      ],
      es: [
        'Cualquier bulto nuevo en el escroto (incluso si es indoloro)',
        'Crecimiento rápido de un bulto escrotal',
        'Dolor o sensibilidad en el testículo',
        'Pesadez o dolor en el escroto',
        'Hinchazón que no desaparece'
      ]
    }
  },
  {
    id: 'painful-ejaculation',
    title: {
      en: 'Painful Ejaculation',
      es: 'Eyaculación Dolorosa'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/6/63/Gray1156.png',
      alt: {
        en: 'Anatomical diagram of male genitourinary system',
        es: 'Diagrama anatómico del sistema genitourinario masculino'
      },
      source: 'Gray\'s Anatomy / Wikimedia Commons (Public Domain)',
      caption: {
        en: 'Pain during ejaculation can originate from the prostate, seminal vesicles, or urethra',
        es: 'El dolor durante la eyaculación puede originarse en la próstata, vesículas seminales o uretra'
      }
    },
    tags: ['painful ejaculation', 'ejaculation pain', 'orgasm pain', 'prostatitis', 'pelvic pain'],
    relatedTopics: ['prostate', 'pelvic-floor-pt', 'erectile-dysfunction'],
    content: {
      en: `Pain with ejaculation is a sensitive and often embarrassing problem, but it is more common than many men realize. Painful ejaculation can feel like burning, aching, pressure, or sharp discomfort in the penis, testicles, perineum, or lower abdomen at the time of orgasm.

There are many possible causes. Infections or inflammation of the prostate, urethra, or seminal vesicles are frequent reasons. Chronic prostatitis or pelvic floor muscle tension can also lead to pain during ejaculation. Sometimes prior surgeries, medications, or psychological stress can play a role.

In many cases, the problem is temporary and improves on its own. When it persists, a medical evaluation is important. Treatment depends on the cause and may include antibiotics, anti-inflammatory medications, pelvic floor physical therapy, or changes in certain medications.

Painful ejaculation is not something men need to suffer through in silence. Most causes are treatable, and a urologist can help identify the source and develop a plan to improve symptoms.`,
      es: `El dolor con la eyaculación es un problema sensible y a menudo vergonzoso, pero es más común de lo que muchos hombres se dan cuenta. La eyaculación dolorosa puede sentirse como ardor, dolor, presión o molestia aguda en el pene, los testículos, el perineo o la parte baja del abdomen en el momento del orgasmo.

Hay muchas causas posibles. Las infecciones o inflamación de la próstata, la uretra o las vesículas seminales son razones frecuentes. La prostatitis crónica o la tensión de los músculos del suelo pélvico también pueden causar dolor durante la eyaculación. A veces, cirugías previas, medicamentos o estrés psicológico pueden jugar un papel.

En muchos casos, el problema es temporal y mejora por sí solo. Cuando persiste, una evaluación médica es importante. El tratamiento depende de la causa y puede incluir antibióticos, medicamentos antiinflamatorios, fisioterapia del suelo pélvico o cambios en ciertos medicamentos.

La eyaculación dolorosa no es algo que los hombres necesiten sufrir en silencio. La mayoría de las causas son tratables, y un urólogo puede ayudar a identificar la fuente y desarrollar un plan para mejorar los síntomas.`
    },
    whenToCall: {
      en: [
        'Pain that happens every time you ejaculate',
        'Blood in semen',
        'Fever or chills',
        'Discharge from the penis',
        'Pain that spreads to the back or legs',
        'Difficulty urinating'
      ],
      es: [
        'Dolor que ocurre cada vez que eyaculas',
        'Sangre en el semen',
        'Fiebre o escalofríos',
        'Secreción del pene',
        'Dolor que se extiende a la espalda o las piernas',
        'Dificultad para orinar'
      ]
    }
  },
  {
    id: 'nocturia',
    title: {
      en: 'Nocturia Basics',
      es: 'Conceptos Básicos de la Nocturia'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/1/18/Illu_urinary_system.jpg',
      alt: {
        en: 'Diagram of the urinary system showing bladder and kidneys',
        es: 'Diagrama del sistema urinario mostrando vejiga y riñones'
      },
      source: 'Wikimedia Commons (Public Domain)',
      caption: {
        en: 'The kidneys filter blood and produce urine, which is stored in the bladder',
        es: 'Los riñones filtran la sangre y producen orina, que se almacena en la vejiga'
      }
    },
    tags: ['nocturia', 'nighttime urination', 'waking up to urinate', 'frequent urination', 'sleep'],
    relatedTopics: ['bph', 'bladder', 'overactive-bladder'],
    content: {
      en: `Nocturia means waking up at night to urinate. Getting up once per night can be normal, especially as people get older. Waking up two, three, or more times per night is not normal and can seriously affect sleep and quality of life.

Nocturia has many possible causes. Drinking fluids too late in the evening is a common and simple reason. Caffeine and alcohol can increase nighttime urine production. Medical conditions such as overactive bladder, prostate enlargement, diabetes, sleep apnea, and heart or kidney problems can also contribute.

Sometimes the bladder itself is not the main issue. Many people produce too much urine at night, a condition called nocturnal polyuria. Others wake up for unrelated reasons, such as poor sleep, and then decide to use the bathroom simply because they are already awake.

Treatment depends on the underlying cause. Simple lifestyle changes like reducing evening fluids, limiting caffeine, and adjusting medication timing can make a big difference. When symptoms are bothersome, a urologic evaluation can help determine the best approach to improve nighttime sleep.`,
      es: `La nocturia significa despertarse por la noche para orinar. Levantarse una vez por noche puede ser normal, especialmente a medida que las personas envejecen. Despertarse dos, tres o más veces por noche no es normal y puede afectar seriamente el sueño y la calidad de vida.

La nocturia tiene muchas causas posibles. Beber líquidos muy tarde en la noche es una razón común y simple. La cafeína y el alcohol pueden aumentar la producción de orina nocturna. Condiciones médicas como vejiga hiperactiva, agrandamiento de la próstata, diabetes, apnea del sueño y problemas cardíacos o renales también pueden contribuir.

A veces la vejiga en sí no es el problema principal. Muchas personas producen demasiada orina por la noche, una condición llamada poliuria nocturna. Otros se despiertan por razones no relacionadas, como mal sueño, y luego deciden usar el baño simplemente porque ya están despiertos.

El tratamiento depende de la causa subyacente. Cambios simples en el estilo de vida como reducir los líquidos por la noche, limitar la cafeína y ajustar el horario de los medicamentos pueden hacer una gran diferencia. Cuando los síntomas son molestos, una evaluación urológica puede ayudar a determinar el mejor enfoque para mejorar el sueño nocturno.`
    },
    whenToCall: {
      en: [
        'Waking up 3 or more times per night to urinate',
        'Sudden increase in nighttime urination',
        'Pain or burning with urination',
        'Blood in urine',
        'Excessive thirst or unexplained weight loss',
        'Swelling in legs or ankles'
      ],
      es: [
        'Despertarse 3 o más veces por noche para orinar',
        'Aumento repentino de la micción nocturna',
        'Dolor o ardor al orinar',
        'Sangre en la orina',
        'Sed excesiva o pérdida de peso inexplicable',
        'Hinchazón en piernas o tobillos'
      ]
    }
  },
  {
    id: 'urethritis',
    title: {
      en: 'Urethritis Overview',
      es: 'Resumen de Uretritis'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5a/Blausen_0402_FemaleBladder_02.png/500px-Blausen_0402_FemaleBladder_02.png',
      alt: {
        en: 'Diagram of the bladder and urethra',
        es: 'Diagrama de la vejiga y la uretra'
      },
      source: 'Blausen Medical / Wikimedia Commons (CC BY 3.0)',
      caption: {
        en: 'The urethra carries urine from the bladder out of the body',
        es: 'La uretra transporta la orina desde la vejiga hacia afuera del cuerpo'
      }
    },
    tags: ['urethritis', 'burning urination', 'urethral infection', 'discharge', 'sti'],
    relatedTopics: ['uti', 'urethra', 'genital-warts'],
    content: {
      en: `Urethritis is inflammation of the urethra, the tube that carries urine out of the body. It often causes burning with urination, itching, discharge, or discomfort at the tip of the penis. In women, symptoms may include burning, urgency, or pelvic discomfort.

The most common cause of urethritis is infection. Sexually transmitted infections such as chlamydia and gonorrhea are frequent culprits, but many cases are caused by other bacteria that are not sexually transmitted. Irritation from soaps, lubricants, or physical trauma can also lead to urethral inflammation.

Diagnosing urethritis usually involves a urine test and sometimes swabs to check for specific infections. Treatment depends on the cause. Bacterial urethritis is typically treated with antibiotics, while irritation-related urethritis improves by avoiding the triggering product or activity.

It is important to seek medical care if symptoms occur, especially if there is discharge, fever, or new sexual partners. Untreated infections can lead to more serious problems, but with proper treatment, urethritis usually resolves quickly.`,
      es: `La uretritis es la inflamación de la uretra, el tubo que transporta la orina fuera del cuerpo. A menudo causa ardor al orinar, picazón, secreción o molestia en la punta del pene. En las mujeres, los síntomas pueden incluir ardor, urgencia o molestia pélvica.

La causa más común de uretritis es la infección. Las infecciones de transmisión sexual como la clamidia y la gonorrea son culpables frecuentes, pero muchos casos son causados por otras bacterias que no se transmiten sexualmente. La irritación por jabones, lubricantes o trauma físico también puede causar inflamación uretral.

El diagnóstico de uretritis generalmente implica una prueba de orina y a veces hisopos para verificar infecciones específicas. El tratamiento depende de la causa. La uretritis bacteriana generalmente se trata con antibióticos, mientras que la uretritis relacionada con irritación mejora al evitar el producto o actividad desencadenante.

Es importante buscar atención médica si ocurren síntomas, especialmente si hay secreción, fiebre o nuevas parejas sexuales. Las infecciones no tratadas pueden llevar a problemas más serios, pero con el tratamiento adecuado, la uretritis generalmente se resuelve rápidamente.`
    },
    whenToCall: {
      en: [
        'Discharge from the penis or urethra',
        'Burning or pain that does not improve',
        'Fever or chills',
        'Pain in the testicles or pelvis',
        'Symptoms after a new sexual partner',
        'Blood in urine'
      ],
      es: [
        'Secreción del pene o la uretra',
        'Ardor o dolor que no mejora',
        'Fiebre o escalofríos',
        'Dolor en los testículos o la pelvis',
        'Síntomas después de una nueva pareja sexual',
        'Sangre en la orina'
      ]
    }
  },
  {
    id: 'epididymitis',
    title: {
      en: 'Epididymitis Explained',
      es: 'Epididimitis Explicada'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Diagram_of_the_testicles_CRUK_048.svg/400px-Diagram_of_the_testicles_CRUK_048.svg.png',
      alt: {
        en: 'Diagram of testicular anatomy showing the epididymis',
        es: 'Diagrama de la anatomía testicular mostrando el epidídimo'
      },
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'The epididymis sits behind the testicle and can become inflamed (epididymitis)',
        es: 'El epidídimo se encuentra detrás del testículo y puede inflamarse (epididimitis)'
      }
    },
    tags: ['epididymitis', 'testicular pain', 'swollen testicle', 'scrotal pain', 'infection'],
    relatedTopics: ['testicle', 'testicular-pain', 'urethritis'],
    content: {
      en: `Epididymitis is inflammation of the epididymis, the small structure behind the testicle that stores and carries sperm. It is a common cause of scrotal pain and swelling, particularly in younger and middle-aged men.

Symptoms usually include pain in one testicle, swelling, tenderness, and sometimes redness or warmth of the scrotum. Some men also experience burning with urination, fever, or urinary frequency.

In younger men, epididymitis is often caused by sexually transmitted infections. In older men, it is more commonly related to urinary tract infections or prostate problems. Occasionally, it can occur without a clear infection and may be related to physical strain or irritation.

Treatment typically involves antibiotics, rest, scrotal support, and anti-inflammatory medications. Most men improve within a few days of starting treatment, though swelling can take longer to fully resolve. Prompt evaluation is important to rule out more urgent conditions such as testicular torsion.`,
      es: `La epididimitis es la inflamación del epidídimo, la pequeña estructura detrás del testículo que almacena y transporta esperma. Es una causa común de dolor e hinchazón escrotal, particularmente en hombres jóvenes y de mediana edad.

Los síntomas generalmente incluyen dolor en un testículo, hinchazón, sensibilidad y a veces enrojecimiento o calor del escroto. Algunos hombres también experimentan ardor al orinar, fiebre o frecuencia urinaria.

En hombres más jóvenes, la epididimitis a menudo es causada por infecciones de transmisión sexual. En hombres mayores, está más comúnmente relacionada con infecciones del tracto urinario o problemas de próstata. Ocasionalmente, puede ocurrir sin una infección clara y puede estar relacionada con esfuerzo físico o irritación.

El tratamiento típicamente implica antibióticos, descanso, soporte escrotal y medicamentos antiinflamatorios. La mayoría de los hombres mejoran dentro de unos días de comenzar el tratamiento, aunque la hinchazón puede tardar más en resolverse completamente. La evaluación rápida es importante para descartar condiciones más urgentes como la torsión testicular.`
    },
    whenToCall: {
      en: [
        'Sudden severe testicle pain (could be torsion – emergency)',
        'Testicle pain with fever',
        'Swelling that gets worse despite treatment',
        'Nausea or vomiting with testicle pain',
        'Redness spreading beyond the scrotum',
        'Pain that does not improve after a few days of treatment'
      ],
      es: [
        'Dolor testicular severo repentino (podría ser torsión – emergencia)',
        'Dolor testicular con fiebre',
        'Hinchazón que empeora a pesar del tratamiento',
        'Náuseas o vómitos con dolor testicular',
        'Enrojecimiento que se extiende más allá del escroto',
        'Dolor que no mejora después de unos días de tratamiento'
      ]
    }
  },
  {
    id: 'varicoceles',
    title: {
      en: 'Varicoceles – Common Questions',
      es: 'Varicoceles – Preguntas Comunes'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/0/06/Varicocele.png',
      alt: {
        en: 'Diagram illustrating a varicocele',
        es: 'Diagrama que ilustra un varicocele'
      },
      source: 'Blausen Medical / Wikimedia Commons (CC BY 3.0)',
      caption: {
        en: 'A varicocele is an enlargement of the veins in the scrotum, similar to varicose veins',
        es: 'Un varicocele es un agrandamiento de las venas en el escroto, similar a las venas varicosas'
      }
    },
    tags: ['varicocele', 'scrotal veins', 'fertility veins', 'testicular veins', 'infertility'],
    relatedTopics: ['testicle', 'varicocele', 'male-infertility'],
    content: {
      en: `A varicocele is an enlargement of the veins in the scrotum, similar to varicose veins in the legs. Varicoceles are very common and occur in about 15 percent of men. They are usually found on the left side and often feel like a soft bag of worms above the testicle.

Most varicoceles cause no symptoms and are discovered during a routine exam. Some men experience a dull ache or heaviness in the scrotum, especially after standing for long periods. Varicoceles can sometimes affect sperm quality and may play a role in male infertility.

Diagnosis is usually simple and may include a physical exam or scrotal ultrasound. Treatment is not needed unless the varicocele causes pain, affects fertility, or leads to shrinkage of the testicle. When treatment is required, minor surgical or radiologic procedures can correct the problem.

For most men, a varicocele is a harmless condition that simply needs monitoring and reassurance.`,
      es: `Un varicocele es un agrandamiento de las venas en el escroto, similar a las venas varicosas en las piernas. Los varicoceles son muy comunes y ocurren en aproximadamente el 15 por ciento de los hombres. Generalmente se encuentran en el lado izquierdo y a menudo se sienten como una bolsa suave de gusanos sobre el testículo.

La mayoría de los varicoceles no causan síntomas y se descubren durante un examen de rutina. Algunos hombres experimentan un dolor sordo o pesadez en el escroto, especialmente después de estar de pie por largos períodos. Los varicoceles a veces pueden afectar la calidad del esperma y pueden jugar un papel en la infertilidad masculina.

El diagnóstico generalmente es simple y puede incluir un examen físico o una ecografía escrotal. El tratamiento no es necesario a menos que el varicocele cause dolor, afecte la fertilidad o lleve a la reducción del testículo. Cuando se requiere tratamiento, procedimientos quirúrgicos o radiológicos menores pueden corregir el problema.

Para la mayoría de los hombres, un varicocele es una condición inofensiva que simplemente necesita monitoreo y tranquilidad.`
    },
    whenToCall: {
      en: [
        'New scrotal lump or swelling',
        'Pain or discomfort in the scrotum',
        'Concerns about fertility',
        'One testicle noticeably smaller than the other',
        'Varicocele that suddenly gets larger'
      ],
      es: [
        'Nuevo bulto o hinchazón escrotal',
        'Dolor o molestia en el escroto',
        'Preocupaciones sobre la fertilidad',
        'Un testículo notablemente más pequeño que el otro',
        'Varicocele que de repente se agranda'
      ]
    }
  },
  {
    id: 'genital-warts',
    title: {
      en: 'Genital Warts Overview',
      es: 'Resumen de Verrugas Genitales'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Papilloma_Virus_%28HPV%29_EM.jpg/500px-Papilloma_Virus_%28HPV%29_EM.jpg',
      alt: {
        en: 'Electron microscope image of human papillomavirus (HPV)',
        es: 'Imagen de microscopio electrónico del virus del papiloma humano (VPH)'
      },
      source: 'NIH / Wikimedia Commons (Public Domain)',
      caption: {
        en: 'Human papillomavirus (HPV) causes genital warts; vaccination is highly effective',
        es: 'El virus del papiloma humano (VPH) causa verrugas genitales; la vacunación es muy efectiva'
      }
    },
    tags: ['genital warts', 'HPV', 'sexually transmitted infection', 'STI', 'warts'],
    relatedTopics: ['urethritis'],
    content: {
      en: `Genital warts are small growths on the genital or anal area caused by certain strains of the human papillomavirus, or HPV. They are one of the most common sexually transmitted infections. Warts may appear as small bumps, clusters, or cauliflower-like growths and are usually painless.

HPV is very common, and many sexually active people are exposed at some point in their lives. Not everyone who carries HPV develops visible warts. The virus can remain dormant for months or even years before symptoms appear.

Genital warts are diagnosed by physical examination. Treatment options include prescription creams, freezing treatments, or minor office procedures to remove the warts. Even after treatment, warts can sometimes return because the underlying virus may still be present.

HPV vaccination is highly effective at preventing the strains that cause most genital warts. Condoms can reduce, but not completely eliminate, the risk of transmission. Anyone who notices new genital growths should seek medical evaluation for proper diagnosis and treatment.`,
      es: `Las verrugas genitales son pequeños crecimientos en el área genital o anal causados por ciertas cepas del virus del papiloma humano, o VPH. Son una de las infecciones de transmisión sexual más comunes. Las verrugas pueden aparecer como pequeños bultos, grupos o crecimientos en forma de coliflor y generalmente son indoloras.

El VPH es muy común, y muchas personas sexualmente activas están expuestas en algún momento de sus vidas. No todos los que portan el VPH desarrollan verrugas visibles. El virus puede permanecer latente durante meses o incluso años antes de que aparezcan los síntomas.

Las verrugas genitales se diagnostican mediante examen físico. Las opciones de tratamiento incluyen cremas recetadas, tratamientos de congelación o procedimientos menores en el consultorio para eliminar las verrugas. Incluso después del tratamiento, las verrugas a veces pueden regresar porque el virus subyacente aún puede estar presente.

La vacunación contra el VPH es altamente efectiva para prevenir las cepas que causan la mayoría de las verrugas genitales. Los condones pueden reducir, pero no eliminar completamente, el riesgo de transmisión. Cualquiera que note nuevos crecimientos genitales debe buscar evaluación médica para un diagnóstico y tratamiento adecuados.`
    },
    whenToCall: {
      en: [
        'Any new bumps or growths in the genital area',
        'Warts that bleed, itch, or cause pain',
        'Warts that spread or multiply',
        'Concerns about HPV or other STIs',
        'Questions about HPV vaccination'
      ],
      es: [
        'Cualquier bulto o crecimiento nuevo en el área genital',
        'Verrugas que sangran, pican o causan dolor',
        'Verrugas que se propagan o multiplican',
        'Preocupaciones sobre el VPH u otras ITS',
        'Preguntas sobre la vacunación contra el VPH'
      ]
    }
  },
  {
    id: 'post-void-dribbling',
    title: {
      en: 'Post-Void Dribbling',
      es: 'Goteo Post-Miccional'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Benign_Prostatic_Hyperplasia_nci-vol-7137-300.jpg',
      alt: {
        en: 'Diagram of normal prostate vs. enlarged prostate',
        es: 'Diagrama de próstata normal vs. próstata agrandada'
      },
      source: 'National Cancer Institute / Wikimedia Commons (Public Domain)',
      caption: {
        en: 'Prostate and pelvic floor health can affect urination patterns',
        es: 'La salud de la próstata y el suelo pélvico pueden afectar los patrones de micción'
      }
    },
    tags: ['post void dribble', 'urine leakage after urination', 'dribbling', 'incontinence', 'pelvic floor'],
    relatedTopics: ['bph', 'pelvic-floor-pt', 'catheter-removal'],
    content: {
      en: `Post-void dribbling refers to the small amount of urine that leaks out after a man thinks he has finished urinating. It is very common and can happen at any age, though it becomes more frequent as men get older.

This problem usually occurs because a small amount of urine remains in the urethra after urination. Weakness or poor coordination of the pelvic floor muscles can prevent complete emptying of the urethra, leading to a few drops of leakage after leaving the bathroom.

Post-void dribbling is not typically a sign of serious disease. It is different from true urinary incontinence and is usually not related to bladder or prostate problems. Simple techniques can help, such as taking extra time at the end of urination, gently pressing behind the scrotum to milk the urethra, or performing pelvic floor exercises.

If dribbling is severe, new, or associated with other urinary symptoms such as weak stream or frequent urination, a urologic evaluation may be helpful. For most men, reassurance and simple behavioral strategies are all that is needed.`,
      es: `El goteo post-miccional se refiere a la pequeña cantidad de orina que sale después de que un hombre piensa que ha terminado de orinar. Es muy común y puede ocurrir a cualquier edad, aunque se vuelve más frecuente a medida que los hombres envejecen.

Este problema generalmente ocurre porque una pequeña cantidad de orina permanece en la uretra después de orinar. La debilidad o mala coordinación de los músculos del suelo pélvico puede prevenir el vaciado completo de la uretra, llevando a unas pocas gotas de fuga después de salir del baño.

El goteo post-miccional típicamente no es un signo de enfermedad grave. Es diferente de la incontinencia urinaria verdadera y generalmente no está relacionado con problemas de vejiga o próstata. Técnicas simples pueden ayudar, como tomarse tiempo extra al final de la micción, presionar suavemente detrás del escroto para vaciar la uretra, o realizar ejercicios del suelo pélvico.

Si el goteo es severo, nuevo o está asociado con otros síntomas urinarios como chorro débil o micción frecuente, una evaluación urológica puede ser útil. Para la mayoría de los hombres, la tranquilidad y estrategias conductuales simples son todo lo que se necesita.`
    },
    whenToCall: {
      en: [
        'Dribbling that is new or suddenly worse',
        'Large amounts of leakage (not just a few drops)',
        'Weak urine stream or difficulty starting urination',
        'Frequent urination or urgency',
        'Pain with urination',
        'Blood in urine'
      ],
      es: [
        'Goteo que es nuevo o que de repente empeora',
        'Grandes cantidades de fuga (no solo unas gotas)',
        'Chorro de orina débil o dificultad para comenzar a orinar',
        'Micción frecuente o urgencia',
        'Dolor al orinar',
        'Sangre en la orina'
      ]
    }
  },
  {
    id: 'emergency-urology',
    title: {
      en: 'Emergency Urology – When to Seek Immediate Care',
      es: 'Urología de Emergencia – Cuándo Buscar Atención Inmediata'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Testicular_torsion.svg/500px-Testicular_torsion.svg.png',
      alt: {
        en: 'Diagram showing testicular torsion, a urologic emergency',
        es: 'Diagrama que muestra la torsión testicular, una emergencia urológica'
      },
      source: 'Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'Testicular torsion cuts off blood supply and requires emergency surgery within hours',
        es: 'La torsión testicular corta el suministro de sangre y requiere cirugía de emergencia'
      }
    },
    tags: ['emergency', 'urgent', 'when to call', 'warning signs', 'symptoms', 'er', 'hospital'],
    relatedTopics: ['kidney-stone-conservative', 'testicular-pain', 'hematuria'],
    content: {
      en: `Some urologic conditions require emergency medical attention. Knowing when to seek immediate care can prevent serious complications and even save your life. This guide covers the major urologic emergencies and what to look out for.

**Urinary Retention (Cannot Urinate)**

If you suddenly cannot urinate at all despite feeling the need to go, this is called acute urinary retention. It can be extremely painful as the bladder fills but cannot empty. This is a medical emergency.

Symptoms include severe lower abdominal pain, a swollen or distended lower belly, and an overwhelming urge to urinate without success. Causes include enlarged prostate, urethral blockage, nerve problems, or medication side effects.

**What to do:** Go to the emergency room immediately. A catheter will be placed to drain the bladder and relieve pain.

---

**Testicular Torsion (Twisted Testicle)**

Testicular torsion occurs when the testicle rotates on its blood supply cord, cutting off blood flow. This is most common in teenage boys and young men but can happen at any age.

Symptoms include sudden, severe testicular pain that comes on quickly (often within minutes), swelling of the scrotum, nausea or vomiting, and one testicle may appear higher than normal. The pain does not improve with rest or position changes.

**What to do:** This is a surgical emergency. The testicle can die within 6 hours without treatment. Go to the emergency room immediately. Surgery is usually needed within a few hours to save the testicle.

---

**Kidney Stone with Infection (Infected Obstructing Stone)**

A kidney stone blocking the ureter is painful but not always an emergency. However, if the blockage causes infection, this becomes a life-threatening emergency called sepsis.

Warning signs include fever (especially above 101°F or 38.3°C), shaking chills, flank pain on one side, nausea or vomiting, confusion or altered mental status, and rapid heartbeat. If you have a known kidney stone AND develop fever, this is dangerous.

**What to do:** Go to the emergency room immediately. An infected obstructed kidney requires urgent drainage, usually with a stent or nephrostomy tube, along with IV antibiotics.

---

**Priapism (Prolonged Erection)**

Priapism is an erection lasting more than 4 hours that occurs without sexual stimulation. It is painful and can cause permanent damage to the penis.

This can be caused by certain medications, blood disorders like sickle cell disease, recreational drug use, or sometimes occurs without clear cause.

**What to do:** Seek emergency care if an erection lasts more than 4 hours. Treatment involves draining blood from the penis with a needle and sometimes medication injections.

---

**Severe Hematuria (Heavy Bleeding in Urine)**

Some blood in the urine is common and not always an emergency. However, heavy bleeding with clots that block urination requires immediate attention.

Warning signs include bright red blood with large clots, inability to urinate because of clots, dizziness or lightheadedness from blood loss, and severe pain.

**What to do:** Go to the emergency room. A catheter may be needed to flush out clots, and the source of bleeding needs to be found.

---

**Fournier's Gangrene (Genital Infection)**

This is a rare but life-threatening infection of the skin and tissue around the genitals and perineum. It spreads very quickly and can be fatal without emergency treatment.

Warning signs include severe pain in the genital or rectal area, redness and swelling that spreads rapidly, fever and chills, foul-smelling discharge, and skin that looks dark, blistered, or crackly. People with diabetes are at higher risk.

**What to do:** This is a surgical emergency. Go to the emergency room immediately. Treatment requires IV antibiotics and emergency surgery to remove infected tissue.

---

**Paraphimosis (Trapped Foreskin)**

Paraphimosis occurs when the foreskin of an uncircumcised penis is pulled back and cannot be returned to its normal position, trapping blood flow.

Symptoms include swelling of the foreskin and head of the penis, severe pain, and the foreskin appearing as a tight band behind the head of the penis.

**What to do:** This requires emergency treatment to prevent permanent damage. Go to the emergency room where the foreskin will be reduced.

---

**Summary – When to Go to the Emergency Room**

Seek emergency care immediately if you have:
• Complete inability to urinate
• Sudden severe testicular pain
• Fever with flank pain or known kidney stone
• Erection lasting more than 4 hours
• Heavy bleeding with clots blocking urination
• Rapidly spreading genital infection with fever
• Trapped foreskin with swelling`,
      es: `Algunas condiciones urológicas requieren atención médica de emergencia. Saber cuándo buscar atención inmediata puede prevenir complicaciones serias e incluso salvar tu vida. Esta guía cubre las principales emergencias urológicas y qué buscar.

**Retención Urinaria (No Puede Orinar)**

Si de repente no puede orinar a pesar de sentir la necesidad de hacerlo, esto se llama retención urinaria aguda. Puede ser extremadamente doloroso ya que la vejiga se llena pero no puede vaciarse. Esta es una emergencia médica.

Los síntomas incluyen dolor severo en el abdomen bajo, un vientre hinchado o distendido, y una necesidad abrumadora de orinar sin éxito.

**Qué hacer:** Vaya a la sala de emergencias inmediatamente. Se colocará un catéter para drenar la vejiga y aliviar el dolor.

---

**Torsión Testicular (Testículo Retorcido)**

La torsión testicular ocurre cuando el testículo gira sobre su cordón de suministro de sangre, cortando el flujo sanguíneo. Es más común en adolescentes y hombres jóvenes pero puede ocurrir a cualquier edad.

Los síntomas incluyen dolor testicular severo y repentino que aparece rápidamente (a menudo en minutos), hinchazón del escroto, náuseas o vómitos, y un testículo puede parecer más alto de lo normal.

**Qué hacer:** Esta es una emergencia quirúrgica. El testículo puede morir en 6 horas sin tratamiento. Vaya a la sala de emergencias inmediatamente.

---

**Cálculo Renal con Infección**

Un cálculo renal que bloquea el uréter es doloroso pero no siempre una emergencia. Sin embargo, si el bloqueo causa infección, esto se convierte en una emergencia potencialmente mortal llamada sepsis.

Las señales de advertencia incluyen fiebre (especialmente por encima de 38.3°C), escalofríos intensos, dolor en el costado de un lado, náuseas o vómitos, confusión, y latidos cardíacos rápidos.

**Qué hacer:** Vaya a la sala de emergencias inmediatamente. Un riñón obstruido infectado requiere drenaje urgente.

---

**Priapismo (Erección Prolongada)**

El priapismo es una erección que dura más de 4 horas y ocurre sin estimulación sexual. Es doloroso y puede causar daño permanente al pene.

**Qué hacer:** Busque atención de emergencia si una erección dura más de 4 horas.

---

**Hematuria Severa (Sangrado Abundante en la Orina)**

El sangrado abundante con coágulos que bloquean la micción requiere atención inmediata.

Las señales de advertencia incluyen sangre roja brillante con coágulos grandes, incapacidad para orinar debido a los coágulos, mareos por pérdida de sangre, y dolor severo.

**Qué hacer:** Vaya a la sala de emergencias.

---

**Gangrena de Fournier (Infección Genital)**

Esta es una infección rara pero potencialmente mortal de la piel y el tejido alrededor de los genitales. Se propaga muy rápidamente y puede ser fatal sin tratamiento de emergencia.

Las señales de advertencia incluyen dolor severo en el área genital o rectal, enrojecimiento e hinchazón que se propagan rápidamente, fiebre y escalofríos, secreción maloliente, y piel que se ve oscura, ampollada o agrietada.

**Qué hacer:** Esta es una emergencia quirúrgica. Vaya a la sala de emergencias inmediatamente.

---

**Parafimosis (Prepucio Atrapado)**

La parafimosis ocurre cuando el prepucio de un pene no circuncidado se retrae y no puede volver a su posición normal, atrapando el flujo sanguíneo.

**Qué hacer:** Esto requiere tratamiento de emergencia. Vaya a la sala de emergencias.

---

**Resumen – Cuándo Ir a la Sala de Emergencias**

Busque atención de emergencia inmediatamente si tiene:
• Incapacidad completa para orinar
• Dolor testicular severo y repentino
• Fiebre con dolor en el costado o cálculo renal conocido
• Erección que dura más de 4 horas
• Sangrado abundante con coágulos que bloquean la micción
• Infección genital que se propaga rápidamente con fiebre
• Prepucio atrapado con hinchazón`
    },
    whenToCall: {
      en: [
        'Complete inability to urinate',
        'Sudden severe testicular pain',
        'Fever with flank pain or known kidney stone',
        'Erection lasting more than 4 hours',
        'Heavy bleeding with clots blocking urination',
        'Rapidly spreading genital infection with fever',
        'Trapped foreskin with swelling'
      ],
      es: [
        'Incapacidad completa para orinar',
        'Dolor testicular severo y repentino',
        'Fiebre con dolor en el costado o cálculo renal conocido',
        'Erección que dura más de 4 horas',
        'Sangrado abundante con coágulos que bloquean la micción',
        'Infección genital que se propaga rápidamente con fiebre',
        'Prepucio atrapado con hinchazón'
      ]
    }
  },
  {
    id: 'bph-surgical-treatments',
    title: {
      en: 'BPH Surgical Treatments – A Complete Guide',
      es: 'Tratamientos Quirúrgicos para HPB – Una Guía Completa'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Benign_Prostatic_Hyperplasia_nci-vol-7137-300.jpg',
      alt: {
        en: 'Diagram comparing normal prostate vs. enlarged prostate (BPH)',
        es: 'Diagrama comparando próstata normal vs. próstata agrandada (HPB)'
      },
      source: 'National Cancer Institute / Wikimedia Commons (Public Domain)',
      caption: {
        en: 'Left: Normal prostate. Right: Enlarged prostate (BPH) compressing the urethra and blocking urine flow.',
        es: 'Izquierda: Próstata normal. Derecha: Próstata agrandada (HPB) comprimiendo la uretra y bloqueando el flujo de orina.'
      }
    },
    tags: ['bph', 'enlarged prostate', 'turp', 'holep', 'rezum', 'urolift', 'surgery', 'procedure'],
    relatedTopics: ['bph', 'prostate', 'urinary-retention-catheter'],
    content: {
      en: `Benign prostatic hyperplasia (BPH), or enlarged prostate, affects most men as they age. When medications don't provide enough relief, several surgical options can help. This guide reviews the main procedures available today.

**When Is Surgery Considered?**

Surgery is typically recommended when:
• Medications haven't adequately controlled symptoms
• You have recurrent urinary retention (unable to urinate)
• You develop bladder stones or kidney problems from BPH
• You have recurrent urinary tract infections
• You want to stop taking daily BPH medications

Your urologist will consider your prostate size, symptoms, overall health, and personal preferences when recommending the best approach.

---

**TURP (Transurethral Resection of the Prostate)**

TURP has been the "gold standard" surgical treatment for BPH for decades. A scope is inserted through the urethra, and an electrical loop is used to remove prostate tissue piece by piece.

**Best for:** Moderate to large prostates (30-80 grams)

**Pros:**
• Proven, long-lasting results
• Significant symptom improvement (70-90% of patients)
• Covered by most insurance

**Cons:**
• Requires anesthesia and hospital stay (usually 1-2 days)
• Catheter needed for several days
• Risk of bleeding
• Retrograde ejaculation in 50-75% of patients (semen goes into bladder instead of out)
• Recovery takes 4-6 weeks

---

**HoLEP (Holmium Laser Enucleation of the Prostate)**

HoLEP uses a laser to remove prostate tissue in larger pieces, which are then morcellated (broken up) and removed. It's especially effective for very large prostates.

**Best for:** Large to very large prostates (over 80 grams)

**Pros:**
• Can treat any size prostate
• Less bleeding than TURP
• Shorter hospital stay (often same-day)
• Very durable results
• Lower retreatment rates

**Cons:**
• Requires specialized expertise (not available everywhere)
• Retrograde ejaculation common
• Temporary urinary urgency during healing
• Longer learning curve for surgeons

---

**Rezum (Water Vapor Thermal Therapy)**

Rezum uses steam (water vapor) to destroy excess prostate tissue. The procedure takes only a few minutes and can be done in the office.

**Best for:** Small to moderate prostates (30-80 grams) with median lobe involvement

**Pros:**
• Minimally invasive, office-based
• Preserves sexual function in most men
• Quick procedure (under 10 minutes)
• No incisions
• Good symptom relief

**Cons:**
• Temporary catheter needed (3-7 days)
• Results take 2-3 months to fully develop
• May not work for very large prostates
• Less long-term data than TURP

---

**UroLift (Prostatic Urethral Lift)**

UroLift uses tiny implants to lift and hold enlarged prostate tissue out of the way, opening the urethra without removing any tissue.

**Best for:** Small to moderate prostates (under 80 grams) without a large median lobe

**Pros:**
• Preserves sexual function (no retrograde ejaculation)
• Quick recovery (most return to normal activity in days)
• Can be done in office under local anesthesia
• No cutting, heating, or tissue removal

**Cons:**
• Not suitable for all prostate shapes (especially large median lobes)
• Less dramatic improvement than tissue-removing procedures
• Some men need additional treatment later
• Permanent implants remain in place

---

**GreenLight Laser (PVP - Photoselective Vaporization)**

GreenLight uses a high-powered laser to vaporize prostate tissue. It's similar to TURP but with less bleeding.

**Best for:** Men on blood thinners or at high bleeding risk; moderate-sized prostates

**Pros:**
• Less bleeding than TURP (good for patients on blood thinners)
• Shorter catheter time
• Often outpatient

**Cons:**
• May not remove as much tissue as TURP
• Retrograde ejaculation still common
• Some irritative symptoms during healing

---

**Aquablation (Waterjet Ablation)**

Aquablation uses a high-pressure waterjet guided by robotic imaging to remove prostate tissue. It's a newer technology combining precision with tissue removal.

**Best for:** Moderate to large prostates (30-150 grams)

**Pros:**
• Highly precise, robotic-assisted
• Can treat large prostates
• Shorter procedure time than HoLEP
• Lower rate of retrograde ejaculation than TURP

**Cons:**
• Not widely available yet
• Requires specialized equipment
• Limited long-term data
• Still requires anesthesia

---

**Simple Prostatectomy (Open or Robotic)**

For extremely large prostates (over 100-150 grams), open or robotic simple prostatectomy removes the entire inner portion of the prostate through an incision.

**Best for:** Very large prostates not suitable for other procedures

**Pros:**
• Very effective for massive prostates
• Definitive treatment

**Cons:**
• Major surgery with longer recovery
• Higher risk of complications
• Longer hospital stay

---

**How to Choose**

Consider these factors when discussing options with your urologist:
• **Prostate size:** Larger prostates may need HoLEP or simple prostatectomy
• **Sexual function:** UroLift and Rezum are better at preserving ejaculation
• **Recovery time:** UroLift and Rezum have faster recovery
• **Durability:** TURP and HoLEP have the longest track records
• **Availability:** Not all procedures are available at every center
• **Your health:** Some procedures are safer for men with bleeding risks or other conditions

There is no single "best" procedure – the right choice depends on your individual situation. Have an open discussion with your urologist about what matters most to you.`,
      es: `La hiperplasia prostática benigna (HPB), o próstata agrandada, afecta a la mayoría de los hombres a medida que envejecen. Cuando los medicamentos no brindan suficiente alivio, varias opciones quirúrgicas pueden ayudar.

**¿Cuándo Se Considera la Cirugía?**

La cirugía generalmente se recomienda cuando:
• Los medicamentos no han controlado adecuadamente los síntomas
• Tiene retención urinaria recurrente
• Desarrolla cálculos en la vejiga o problemas renales por HPB
• Tiene infecciones urinarias recurrentes
• Quiere dejar de tomar medicamentos diarios para HPB

---

**RTUP (Resección Transuretral de Próstata)**

La RTUP ha sido el tratamiento quirúrgico "estándar de oro" para la HPB durante décadas. Se inserta un endoscopio a través de la uretra y se usa un asa eléctrica para extraer tejido prostático.

**Mejor para:** Próstatas moderadas a grandes (30-80 gramos)

**Ventajas:**
• Resultados comprobados y duraderos
• Mejora significativa de los síntomas (70-90% de los pacientes)

**Desventajas:**
• Requiere anestesia y hospitalización
• Se necesita catéter por varios días
• Riesgo de sangrado
• Eyaculación retrógrada en 50-75% de los pacientes

---

**HoLEP (Enucleación con Láser de Holmio)**

El HoLEP usa un láser para extraer tejido prostático en piezas más grandes. Es especialmente efectivo para próstatas muy grandes.

**Mejor para:** Próstatas grandes a muy grandes (más de 80 gramos)

**Ventajas:**
• Puede tratar cualquier tamaño de próstata
• Menos sangrado que la RTUP
• Resultados muy duraderos

**Desventajas:**
• Requiere experiencia especializada
• Eyaculación retrógrada común

---

**Rezum (Terapia Térmica con Vapor de Agua)**

Rezum usa vapor para destruir el exceso de tejido prostático. El procedimiento toma solo unos minutos y puede realizarse en el consultorio.

**Mejor para:** Próstatas pequeñas a moderadas (30-80 gramos)

**Ventajas:**
• Mínimamente invasivo, en consultorio
• Preserva la función sexual en la mayoría de los hombres
• Procedimiento rápido

**Desventajas:**
• Se necesita catéter temporal
• Los resultados tardan 2-3 meses en desarrollarse completamente

---

**UroLift (Elevación Uretral Prostática)**

UroLift usa pequeños implantes para levantar y sostener el tejido prostático agrandado, abriendo la uretra sin extraer tejido.

**Mejor para:** Próstatas pequeñas a moderadas (menos de 80 gramos)

**Ventajas:**
• Preserva la función sexual (sin eyaculación retrógrada)
• Recuperación rápida
• Puede hacerse en consultorio

**Desventajas:**
• No es adecuado para todas las formas de próstata
• Mejora menos dramática que los procedimientos que extraen tejido

---

**Cómo Elegir**

Considere estos factores al discutir opciones con su urólogo:
• **Tamaño de la próstata**
• **Función sexual**
• **Tiempo de recuperación**
• **Durabilidad**
• **Disponibilidad**
• **Su salud general**

No existe un procedimiento "mejor" único – la elección correcta depende de su situación individual.`
    },
    whenToCall: {
      en: [
        'Unable to urinate after BPH surgery',
        'Heavy bleeding (soaking more than one pad per hour)',
        'Fever over 101°F (38.3°C)',
        'Severe pain not controlled by prescribed medications',
        'Signs of infection (increasing redness, swelling, discharge)',
        'Blood clots blocking urination'
      ],
      es: [
        'Incapacidad para orinar después de la cirugía',
        'Sangrado abundante (empapando más de una toalla por hora)',
        'Fiebre superior a 38.3°C',
        'Dolor severo no controlado por medicamentos recetados',
        'Signos de infección (enrojecimiento, hinchazón, secreción)',
        'Coágulos de sangre que bloquean la micción'
      ]
    }
  }
];

// Export list of article IDs for routing
export const commonConcernsIds = commonConcernsArticles.map(a => a.id);
